import { useState, useEffect } from 'react';
import { Sidebar } from '../layout/Sidebar';
import { TopBar } from '../layout/TopBar';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useRestaurant } from '../../contexts/RestaurantContext';
import { useCurrency } from '../../hooks/useCurrency';
import { customerTypesService, CustomerType } from '../../services/customerTypes.service';
import {
  customersService,
  Customer,
  CustomerTransaction,
  CreateTransactionDto,
  TransactionType,
} from '../../services/customers.service';
import { toast } from 'sonner';
import {
  CreditCard,
  Search,
  Receipt,
  Printer,
  X,
  Plus,
  RefreshCw,
} from 'lucide-react';

// ─── Types / constants ───────────────────────────────────────────────────────

const TX_TYPES: { value: TransactionType; label: string }[] = [
  { value: 'PAYMENT', label: 'Payment' },
  { value: 'REFUND',  label: 'Refund'  },
  { value: 'CREDIT',  label: 'Credit'  },
  { value: 'CHARGE',  label: 'Charge'  },
];

const TX_TYPE_BADGE: Record<string, string> = {
  PAYMENT: 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400',
  REFUND:  'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400',
  CREDIT:  'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400',
  CHARGE:  'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400',
};

// ─── Receipt Modal ────────────────────────────────────────────────────────────

interface ReceiptModalProps {
  transaction: CustomerTransaction;
  customer: Customer | null;
  businessName: string;
  businessAddress?: string | null;
  businessPhone?: string | null;
  businessEmail?: string | null;
  formatCurrency: (n: number | string) => string;
  onClose: () => void;
}

interface ReceiptContentProps {
  transaction: CustomerTransaction;
  customer: Customer | null;
  businessName: string;
  businessAddress?: string | null;
  businessPhone?: string | null;
  businessEmail?: string | null;
  formatCurrency: (n: number | string) => string;
  txDate: string;
  rawAmount: number | string;
}

function ReceiptContent({ transaction, customer, businessName, businessAddress, businessPhone, businessEmail, formatCurrency, txDate, rawAmount }: ReceiptContentProps) {
  return (
    <div className="w-full py-4">
      <div className="text-center mb-6">
        <p className="text-2xl font-bold text-gray-900">{businessName}</p>
        {businessAddress && <p className="text-xs text-gray-500 mt-1">{businessAddress}</p>}
        {(businessPhone || businessEmail) && (
          <p className="text-xs text-gray-500 mt-0.5">
            {[businessPhone, businessEmail].filter(Boolean).join('  ·  ')}
          </p>
        )}
        <p className="text-sm text-gray-400 mt-1">Transaction Receipt</p>
      </div>

      <div className="border-t border-dashed border-gray-300 my-4" />

      <div className="space-y-3 text-sm">
        <div className="flex items-start justify-between gap-6">
          <span className="text-gray-500 flex-shrink-0">Receipt #</span>
          <span className="font-medium text-gray-900 text-right break-all">{transaction.receiptNumber}</span>
        </div>
        <div className="flex items-center justify-between gap-6">
          <span className="text-gray-500 flex-shrink-0">Date</span>
          <span className="font-medium text-gray-900">{txDate}</span>
        </div>
        <div className="flex items-center justify-between gap-6">
          <span className="text-gray-500 flex-shrink-0">Type</span>
          <span className="font-semibold text-gray-900">{transaction.type}</span>
        </div>
      </div>

      <div className="border-t border-dashed border-gray-300 my-4" />

      {customer && (
        <>
          <div className="space-y-3 text-sm mb-4">
            <div className="flex items-center justify-between gap-6">
              <span className="text-gray-500 flex-shrink-0">Customer</span>
              <span className="font-medium text-gray-900 text-right">{customer.firstName} {customer.lastName}</span>
            </div>
            {customer.customerNumber && (
              <div className="flex items-center justify-between gap-6">
                <span className="text-gray-500 flex-shrink-0">Cust. No.</span>
                <span className="font-medium text-gray-900">{customer.customerNumber}</span>
              </div>
            )}
            {customer.phone && (
              <div className="flex items-center justify-between gap-6">
                <span className="text-gray-500 flex-shrink-0">Phone</span>
                <span className="font-medium text-gray-900">{customer.phone}</span>
              </div>
            )}
          </div>
          <div className="border-t border-dashed border-gray-300 my-4" />
        </>
      )}

      <div className="space-y-3 text-sm">
        <div className="flex items-start justify-between gap-6">
          <span className="text-gray-500 flex-shrink-0">Description</span>
          <span className="font-medium text-gray-900 text-right">{transaction.description}</span>
        </div>
        {transaction.notes && (
          <div className="flex items-start justify-between gap-6">
            <span className="text-gray-500 flex-shrink-0">Notes</span>
            <span className="text-gray-700 text-right">{transaction.notes}</span>
          </div>
        )}
      </div>

      <div className="border-t border-dashed border-gray-300 my-5" />

      <div className="flex justify-between items-center gap-4 py-2">
        <span className="text-lg font-bold text-gray-900">Total</span>
        <span className="text-2xl font-bold text-gray-900">
          {transaction.type === 'REFUND' || transaction.type === 'CHARGE' ? '-' : ''}
          {formatCurrency(rawAmount)}
        </span>
      </div>

      <div className="border-t border-dashed border-gray-300 my-5" />

      <p className="text-center text-sm text-gray-400 mt-4">Thank you for your business!</p>
    </div>
  );
}

export function TransactionReceiptModal({
  transaction,
  customer,
  businessName,
  businessAddress,
  businessPhone,
  businessEmail,
  formatCurrency,
  onClose,
}: ReceiptModalProps) {
  const handlePrint = () => window.print();

  const txDate = new Date(transaction.transactionDate).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
  const rawAmount = transaction.amount; // keep as-is (string | number from API)

  return (
    <>
      {/* On-screen modal (hidden during print) */}
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 print:hidden">
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-sm">
          {/* Toolbar */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800">
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Receipt className="w-4 h-4 text-indigo-600" />
              Transaction Receipt
            </h3>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>

          {/* Receipt preview (screen) */}
          <div className="px-5 py-4">
            <ReceiptContent
              transaction={transaction}
              customer={customer}
              businessName={businessName}
              businessAddress={businessAddress}
              businessPhone={businessPhone}
              businessEmail={businessEmail}
              formatCurrency={formatCurrency}
              txDate={txDate}
              rawAmount={rawAmount}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 px-5 pb-5">
            <button
              onClick={onClose}
              className="flex-1 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              Close
            </button>
            <button
              onClick={handlePrint}
              className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-colors"
            >
              <Printer className="w-4 h-4" />
              Print
            </button>
          </div>
        </div>
      </div>

      {/* Print-only receipt — rendered outside the backdrop so print:hidden doesn't affect it */}
      <div id="tx-receipt-print-root" className="hidden print:block fixed inset-0 bg-white px-1 py-4 z-[9999]">
        <style>{`@page { margin: 4mm; }`}</style>
        <ReceiptContent
          transaction={transaction}
          customer={customer}
          businessName={businessName}
          businessAddress={businessAddress}
          businessPhone={businessPhone}
          businessEmail={businessEmail}
          formatCurrency={formatCurrency}
          txDate={txDate}
          rawAmount={rawAmount}
        />
      </div>
    </>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

interface TransactionsPageProps {
  onNavigate?: (page: string) => void;
}

export function TransactionsPage({ onNavigate }: TransactionsPageProps) {
  usePageTitle('Transactions');

  const [sidebarOpen, setSidebarOpen] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth >= 768 : true
  );
  const { restaurantName, restaurantPhone, restaurantEmail, currentBranch } = useRestaurant();
  const { formatCurrency } = useCurrency();
  const restaurantAddress = currentBranch?.address || localStorage.getItem('restaurantAddress') || undefined;

  // Customer type & customer selection
  const [customerTypes, setCustomerTypes] = useState<CustomerType[]>([]);
  const [selectedTypeId, setSelectedTypeId] = useState('');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);

  // Transaction form
  const emptyForm = () => ({
    type: 'PAYMENT' as TransactionType,
    amount: '',
    description: '',
    notes: '',
    transactionDate: new Date().toISOString().slice(0, 10),
  });
  const [txForm, setTxForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  // Transactions history
  const [transactions, setTransactions] = useState<CustomerTransaction[]>([]);
  const [loadingTx, setLoadingTx] = useState(false);

  // Receipt
  const [receiptTx, setReceiptTx] = useState<CustomerTransaction | null>(null);
  const [showReceipt, setShowReceipt] = useState(false);

  // Load customer types on mount
  useEffect(() => {
    customerTypesService.getAll().then((r) => {
      if (r.success && r.data) setCustomerTypes(r.data.filter((t) => t.isActive));
    });
  }, []);

  // Load customers when type changes
  useEffect(() => {
    if (!selectedTypeId) {
      setCustomers([]);
      setSelectedCustomer(null);
      setCustomerSearch('');
      return;
    }
    setLoadingCustomers(true);
    setSelectedCustomer(null);
    setCustomerSearch('');
    customersService
      .getCustomers({ limit: 500 })
      .then((r) => {
        if (r.success && r.data) {
          const list: Customer[] = (r.data as any).data ?? (r.data as any);
          setCustomers(list.filter((c) => c.customerTypeId === selectedTypeId));
        }
      })
      .catch(() => {})
      .finally(() => setLoadingCustomers(false));
  }, [selectedTypeId]);

  // Load transactions when customer changes
  useEffect(() => {
    if (!selectedCustomer) {
      setTransactions([]);
      return;
    }
    setLoadingTx(true);
    customersService
      .getTransactions(selectedCustomer.id)
      .then((r) => {
        if (r.success && r.data) setTransactions(r.data);
      })
      .catch(() => {})
      .finally(() => setLoadingTx(false));
  }, [selectedCustomer]);

  const filteredCustomers = customers.filter((c) => {
    const q = customerSearch.toLowerCase();
    return (
      c.customerNumber?.toLowerCase().includes(q) ||
      `${c.firstName} ${c.lastName}`.toLowerCase().includes(q) ||
      c.phone?.toLowerCase().includes(q)
    );
  });

  const handleSelectCustomer = (c: Customer) => {
    setSelectedCustomer(c);
    setCustomerSearch(
      `${c.customerNumber ? c.customerNumber + ' — ' : ''}${c.firstName} ${c.lastName}`
    );
    setShowCustomerDropdown(false);
  };

  const handleSave = async () => {
    if (!selectedCustomer) {
      toast.error('Please select a customer first.');
      return;
    }
    const parsed = parseFloat(txForm.amount);
    if (!txForm.amount || isNaN(parsed) || parsed <= 0) {
      toast.error('Enter a valid amount greater than 0.');
      return;
    }
    if (!txForm.description.trim()) {
      toast.error('Description is required.');
      return;
    }

    setSaving(true);
    try {
      const dto: CreateTransactionDto = {
        type: txForm.type,
        amount: parsed,
        description: txForm.description.trim(),
        notes: txForm.notes.trim() || undefined,
        transactionDate: txForm.transactionDate
          ? new Date(txForm.transactionDate).toISOString()
          : undefined,
      };
      const res = await customersService.createTransaction(selectedCustomer.id, dto);
      if (res.success && res.data) {
        setTransactions((prev) => [res.data!, ...prev]);
        toast.success('Transaction saved successfully!');
        setReceiptTx(res.data!);
        setShowReceipt(true);
        setTxForm(emptyForm());
      }
    } catch {
      toast.error('Failed to save transaction.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSelectedTypeId('');
    setSelectedCustomer(null);
    setCustomerSearch('');
    setTransactions([]);
    setTxForm(emptyForm());
  };

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-950">
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        activePage="transactions"
        onNavigate={onNavigate}
      />

      <div
        className={`flex-1 flex flex-col overflow-hidden transition-all duration-300 ${
          sidebarOpen ? 'min-[768px]:ml-64' : 'ml-0'
        }`}
      >
        <TopBar
          onMenuClick={() => setSidebarOpen(!sidebarOpen)}
          sidebarOpen={sidebarOpen}
          onNavigate={onNavigate}
        />

        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-5xl mx-auto">
            {/* Header */}
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <CreditCard className="w-6 h-6 text-indigo-600" />
                  Transactions
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Record customer payments, credits and charges
                </p>
              </div>
              <button
                onClick={handleReset}
                className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Reset
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* ── Left: New Transaction Form ── */}
              <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-6 shadow-sm">
                <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-5">
                  New Transaction
                </h2>

                {/* Customer Type */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Customer Type
                  </label>
                  <select
                    value={selectedTypeId}
                    onChange={(e) => setSelectedTypeId(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Select a customer type…</option>
                    {customerTypes.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Customer Search */}
                <div className="mb-4 relative">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Customer Number / Name
                  </label>
                  {loadingCustomers ? (
                    <div className="flex items-center gap-2 py-2 text-sm text-gray-500 dark:text-gray-400">
                      <RefreshCw className="w-4 h-4 animate-spin" /> Loading customers…
                    </div>
                  ) : (
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                      <input
                        type="text"
                        value={customerSearch}
                        disabled={!selectedTypeId}
                        placeholder={
                          selectedTypeId
                            ? 'Search by number or name…'
                            : 'Select a customer type first'
                        }
                        onChange={(e) => {
                          setCustomerSearch(e.target.value);
                          setSelectedCustomer(null);
                          setShowCustomerDropdown(true);
                        }}
                        onFocus={() => setShowCustomerDropdown(true)}
                        onBlur={() => setTimeout(() => setShowCustomerDropdown(false), 180)}
                        className="w-full pl-9 pr-8 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                      {selectedCustomer && (
                        <button
                          onClick={() => {
                            setSelectedCustomer(null);
                            setCustomerSearch('');
                          }}
                          className="absolute right-2.5 top-1/2 -translate-y-1/2"
                        >
                          <X className="w-4 h-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
                        </button>
                      )}
                    </div>
                  )}

                  {/* Dropdown */}
                  {showCustomerDropdown && filteredCustomers.length > 0 && !selectedCustomer && (
                    <div className="absolute z-20 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl max-h-52 overflow-y-auto">
                      {filteredCustomers.slice(0, 25).map((c) => (
                        <button
                          key={c.id}
                          onMouseDown={() => handleSelectCustomer(c)}
                          className="w-full text-left px-4 py-2.5 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 border-b border-gray-100 dark:border-gray-700 last:border-0 transition-colors"
                        >
                          <span className="font-semibold text-sm text-gray-900 dark:text-white">
                            {c.customerNumber}
                          </span>
                          <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">
                            {c.firstName} {c.lastName}
                          </span>
                          {c.phone && (
                            <span className="text-xs text-gray-400 dark:text-gray-500 ml-2">
                              {c.phone}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Selected customer pill */}
                  {selectedCustomer && (
                    <div className="mt-2 px-3 py-2 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-100 dark:border-indigo-800">
                      <p className="text-sm font-semibold text-indigo-700 dark:text-indigo-400">
                        {selectedCustomer.firstName} {selectedCustomer.lastName}
                      </p>
                      <p className="text-xs text-indigo-500 dark:text-indigo-300 mt-0.5">
                        {selectedCustomer.customerNumber}
                        {selectedCustomer.phone && ` · ${selectedCustomer.phone}`}
                      </p>
                    </div>
                  )}
                </div>

                {/* Transaction Type */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Type
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {TX_TYPES.map((t) => (
                      <button
                        key={t.value}
                        onClick={() => setTxForm((f) => ({ ...f, type: t.value }))}
                        className={`py-2 px-3 rounded-lg border text-sm font-medium transition-colors ${
                          txForm.type === t.value
                            ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400'
                            : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                        }`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Amount */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Amount
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={txForm.amount}
                    onChange={(e) => setTxForm((f) => ({ ...f, amount: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                {/* Description */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Description
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Monthly subscription payment"
                    value={txForm.description}
                    onChange={(e) => setTxForm((f) => ({ ...f, description: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                {/* Notes */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Notes{' '}
                    <span className="font-normal text-gray-400">(optional)</span>
                  </label>
                  <textarea
                    rows={2}
                    placeholder="Additional notes…"
                    value={txForm.notes}
                    onChange={(e) => setTxForm((f) => ({ ...f, notes: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                  />
                </div>

                {/* Date */}
                <div className="mb-5">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Transaction Date
                  </label>
                  <input
                    type="date"
                    value={txForm.transactionDate}
                    onChange={(e) =>
                      setTxForm((f) => ({ ...f, transactionDate: e.target.value }))
                    }
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <button
                  onClick={handleSave}
                  disabled={saving || !selectedCustomer}
                  className="w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors"
                >
                  {saving ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Plus className="w-4 h-4" />
                  )}
                  {saving ? 'Saving…' : 'Save Transaction'}
                </button>
              </div>

              {/* ── Right: Transaction History ── */}
              <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-6 shadow-sm flex flex-col">
                <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-1">
                  Transaction History
                </h2>
                {selectedCustomer && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                    {selectedCustomer.firstName} {selectedCustomer.lastName}
                    {selectedCustomer.customerNumber && (
                      <span className="ml-1 text-indigo-500 dark:text-indigo-400">
                        ({selectedCustomer.customerNumber})
                      </span>
                    )}
                  </p>
                )}
                {!selectedCustomer ? (
                  <div className="flex-1 flex flex-col items-center justify-center py-16 text-center">
                    <Receipt className="w-12 h-12 text-gray-200 dark:text-gray-700 mb-3" />
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Select a customer to view their transactions
                    </p>
                  </div>
                ) : loadingTx ? (
                  <div className="flex-1 flex items-center justify-center py-16">
                    <RefreshCw className="w-5 h-5 text-indigo-600 animate-spin" />
                  </div>
                ) : transactions.length === 0 ? (
                  <div className="flex-1 flex flex-col items-center justify-center py-16 text-center">
                    <Receipt className="w-12 h-12 text-gray-200 dark:text-gray-700 mb-3" />
                    <p className="text-sm text-gray-500 dark:text-gray-400">No transactions yet</p>
                  </div>
                ) : (
                  <div className="space-y-3 overflow-y-auto max-h-[560px] pr-1">
                    {transactions.map((tx) => (
                      <div
                        key={tx.id}
                        className="p-3.5 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span
                                className={`text-xs font-semibold px-2 py-0.5 rounded-full ${TX_TYPE_BADGE[tx.type]}`}
                              >
                                {tx.type}
                              </span>
                              <span className="text-xs text-gray-400 dark:text-gray-500 font-mono truncate">
                                #{tx.receiptNumber}
                              </span>
                            </div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {tx.description}
                            </p>
                            {tx.notes && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                                {tx.notes}
                              </p>
                            )}
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                              {new Date(tx.transactionDate).toLocaleDateString('en-GB', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                              })}
                            </p>
                          </div>
                          <div className="flex flex-col items-end gap-2 shrink-0">
                            <span
                              className={`text-base font-bold ${
                                tx.type === 'PAYMENT' || tx.type === 'CREDIT'
                                  ? 'text-green-600 dark:text-green-400'
                                  : 'text-red-600 dark:text-red-400'
                              }`}
                            >
                              {tx.type === 'REFUND' || tx.type === 'CHARGE' ? '-' : ''}
                              {formatCurrency(tx.amount)}
                            </span>
                            <button
                              onClick={() => {
                                setReceiptTx(tx);
                                setShowReceipt(true);
                              }}
                              className="flex items-center gap-1 text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
                            >
                              <Printer className="w-3 h-3" />
                              Receipt
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Receipt modal */}
      {showReceipt && receiptTx && (
        <TransactionReceiptModal
          transaction={receiptTx}
          customer={selectedCustomer}
          businessName={restaurantName}
          businessAddress={restaurantAddress}
          businessPhone={restaurantPhone}
          businessEmail={restaurantEmail}
          formatCurrency={formatCurrency}
          onClose={() => setShowReceipt(false)}
        />
      )}
    </div>
  );
}
